/**
 * The ZIvapp backend contract.
 *
 * These files carry no logic — they are the JSDoc specification that every
 * provider in ../mock, ../firebase and ../rest implements independently.
 *
 * @typedef {import('./auth.js').AuthService}         AuthService
 * @typedef {import('./chats.js').ChatsService}       ChatsService
 * @typedef {import('./messages.js').MessagesService} MessagesService
 * @typedef {import('./media.js').MediaService}       MediaService
 * @typedef {import('./status.js').StatusService}     StatusService
 * @typedef {import('./presence.js').PresenceService} PresenceService
 * @typedef {import('./calls.js').CallsService}       CallsService
 * @typedef {import('./contacts.js').ContactsService} ContactsService
 * @typedef {import('./channels.js').ChannelService}  ChannelService
 * @typedef {import('./communities.js').CommunityService} CommunityService
 *
 * @typedef {object} ZivappProvider
 * @property {AuthService}     auth
 * @property {ChatsService}    chats
 * @property {MessagesService} messages
 * @property {MediaService}    media
 * @property {StatusService}   status
 * @property {PresenceService} presence
 * @property {CallsService}    calls
 * @property {ContactsService} contacts
 * @property {ChannelService}  channels
 * @property {CommunityService} communities
 */

/** Every service a provider must export. */
export const SERVICE_NAMES = [
  'auth', 'chats', 'messages', 'media', 'status', 'presence', 'calls', 'contacts',
  'channels', 'communities'
]

/**
 * Methods each service must expose. The dev-time conformance check in
 * ../index.js reads this, so a provider cannot silently drift from the spec.
 */
export const REQUIRED_METHODS = {
  auth: [
    'requestCode', 'confirmCode', 'signOut', 'currentUser',
    'onAuthChange', 'updateProfile', 'deleteAccount'
  ],
  chats: [
    'subscribeToList', 'subscribeToChat', 'get', 'getOrCreateDirect',
    'createGroup', 'updateGroup', 'addMembers', 'removeMember', 'setMemberRole',
    'leaveGroup', 'setMuted', 'setPinned', 'setArchived', 'clearMessages', 'deleteChat'
  ],
  messages: [
    'subscribe', 'loadOlder', 'send', 'edit', 'remove',
    'markRead', 'react', 'unreact', 'search'
  ],
  media: [
    'upload', 'remove', 'localPreview'
  ],
  status: [
    'subscribeToFeed', 'subscribeToMine', 'post', 'markViewed', 'getViewers', 'remove'
  ],
  presence: [
    'startHeartbeat', 'watch', 'setTyping', 'watchTyping'
  ],
  calls: [
    'start', 'accept', 'decline', 'end', 'subscribeToIncoming', 'subscribeToCall',
    'sendOffer', 'sendAnswer', 'sendCandidate', 'subscribeToSignals',
    'subscribeToHistory', 'getIceConfig'
  ],
  contacts: [
    'subscribe', 'findByPhone', 'discover', 'add', 'remove',
    'block', 'unblock', 'subscribeToBlocked'
  ],
  /*
   * Note what is absent: getFollowers. A channel's follower list is the one
   * thing it promises never to reveal, and leaving the method out of the
   * contract is a stronger guarantee than guarding an implementation of it.
   */
  channels: [
    'subscribeToMine', 'subscribeToPosts', 'get', 'getByHandle', 'search',
    'create', 'update', 'follow', 'unfollow', 'setMuted',
    'post', 'editPost', 'deletePost', 'react', 'markRead', 'delete'
  ],
  /*
   * Note `members` IS here, where channels deliberately has no equivalent.
   * A community is a place people are in together, so seeing who else is
   * there is the point; a channel is a broadcast, so it is a leak.
   */
  communities: [
    'subscribeToMine', 'subscribeToGroups', 'get', 'create', 'update',
    'addGroup', 'createGroup', 'removeGroup', 'joinGroup',
    'members', 'addMembers', 'removeMember', 'setRole', 'leave', 'delete'
  ]
}
